import { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Box,
  Chip,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DescriptionIcon from '@mui/icons-material/Description';
import CancelIcon from '@mui/icons-material/Cancel';
import ReplayIcon from '@mui/icons-material/Replay';
import { CommonLayout } from '../commonComponents';
import { getApplicationLogs } from '../../redux-store/actions/application';
import { ApplicationLog, ApplicationLogUser } from '../../redux-store/types/application';
import { APPLICATION_LISTING_STATUS_LOGS } from '../../utils/constant';
import { formatShortDateAndTime } from '../../utils/date';

interface StatusConfig {
  icon: typeof CheckCircleIcon;
  color: string;
  highlight?: { bg: string; border: string };
}

type StatusCategory = 'completed' | 'default' | 'draft' | 'rejected' | 'sentBack';

const STATUS_CONFIG: Record<StatusCategory, StatusConfig> = {
  completed: { icon: CheckCircleIcon, color: 'success.main' },
  rejected: {
    icon: CancelIcon,
    color: 'error.main',
    highlight: { bg: '#FEF2F2', border: '#FECACA' },
  },
  draft: { icon: DescriptionIcon, color: 'grey.500' },
  sentBack: { icon: ReplayIcon, color: '#F59E0B', highlight: { bg: '#FFFBEB', border: '#FDE68A' } },
  default: { icon: CheckCircleIcon, color: 'primary.main' },
};

function resolveStatus(
  status?: string | null,
  isApplicationSentBack?: boolean
): { category: StatusCategory; label: string } {
  if (!status) return { category: 'default', label: '' };

  const base = APPLICATION_LISTING_STATUS_LOGS[status] || status;
  const normalized = base.toLowerCase().replace(/\s+/g, '');

  if (normalized === 'completed' || normalized === 'signed' || normalized.startsWith('approved')) {
    return { category: 'completed', label: base };
  }
  if (normalized === 'rejected' || normalized.includes('invitationexpired')) {
    return { category: 'rejected', label: base };
  }
  if (normalized === 'draft') {
    return isApplicationSentBack
      ? { category: 'sentBack', label: 'Draft (Sent Back)' }
      : { category: 'draft', label: base };
  }
  return { category: 'default', label: base };
}

const getUserLabel = (user: ApplicationLogUser): string => {
  const name = user.name?.trim() || 'Unknown';
  const role = user.role?.trim();
  return role ? `${name}(${role})` : name;
};

function ActivityRow({
  log,
  isLast,
  isMobile,
}: {
  log: ApplicationLog;
  isLast: boolean;
  isMobile: boolean;
}) {
  const { category, label } = resolveStatus(log.newStatus, log.isApplicationSentBack);
  const { icon: IconComponent, color, highlight } = STATUS_CONFIG[category];
  const users = log.users?.length ? log.users : null;

  const detailsContent = (
    <Box
      sx={{
        flex: 1,
        ...(highlight && {
          backgroundColor: highlight.bg,
          border: `1px solid ${highlight.border}`,
          borderRadius: '12px',
          p: 1.5,
        }),
      }}>
      <Typography sx={{ fontSize: 16, fontWeight: 500, color: 'text.primary' }}>
        {label || 'N/A'}
      </Typography>
      {users && (
        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
          {users.map((u, i) => (
            <Chip
              key={`user-${i}`}
              label={getUserLabel(u)}
              variant="filled"
              sx={{
                backgroundColor: '#F8FAFC',
                border: '1px solid #E2E8F0',
                borderRadius: '8px',
                color: '#334155',
                fontSize: 12,
                fontWeight: 500,
                height: 30,
                '& .MuiChip-label': { px: 1 },
              }}
            />
          ))}
        </Stack>
      )}
    </Box>
  );

  const rowSx = {
    py: 3,
    borderBottom: isLast ? 'none' : '1px solid #F1F5F9',
    transition: 'all .2s ease',
    '&:hover': { backgroundColor: '#F8FAFC' },
  };

  if (isMobile) {
    return (
      <Box sx={rowSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <IconComponent sx={{ fontSize: 24, color: color }} />
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary' }}>
            {formatShortDateAndTime(log.timestamp)}
          </Typography>
        </Box>
        {detailsContent}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', ...rowSx }}>
      <Box sx={{ width: 180, flexShrink: 0, textAlign: 'right', pr: 2, pt: 0.5 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.secondary' }}>
          {formatShortDateAndTime(log.timestamp)}
        </Typography>
      </Box>
      <Box
        sx={{
          width: 60,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}>
        <IconComponent sx={{ fontSize: 24, color: color }} />
        {!isLast && (
          <Box
            sx={{
              width: 2,
              flex: 1,
              minHeight: 24,
              backgroundColor: '#CBD5E1',
              mt: 0.5,
            }}
          />
        )}
      </Box>
      {detailsContent}
    </Box>
  );
}

export default function ApplicationLogs(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation<{ applicationNumber: string }>();
  const { applicationNumber = '' } = location.state || {};

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [logs, setLogs] = useState<ApplicationLog[]>([]);
  const [isLoading, setLoading] = useState(false);

  const fetchLogs = useCallback(async () => {
    if (!id) {
      return;
    }
    try {
      setLoading(true);
      const response = (await dispatch(getApplicationLogs(id))) as unknown as ApplicationLog[];
      setLogs(Array.isArray(response) ? response : []);
    } catch (e) {
      console.error((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [dispatch, id]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  return (
    <CommonLayout>
      <>
        <Box sx={{ width: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1,
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Tooltip title="Back">
                <IconButton onClick={() => history.goBack()} sx={{ mr: 0.5 }}>
                  <NavigateBeforeIcon sx={{ color: 'primary.main' }} />
                </IconButton>
              </Tooltip>
              <Box>
                <Typography
                  sx={{
                    fontSize: { xs: 22, sm: 26 },
                    fontWeight: 600,
                    color: 'text.primary',
                  }}>
                  Application Logs
                </Typography>
                {applicationNumber && (
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: 'text.secondary',
                      mb: 1,
                    }}>
                    Application ID: {applicationNumber}
                  </Typography>
                )}
              </Box>
            </Box>

            <Tooltip title="Refresh">
              <IconButton onClick={fetchLogs} disabled={isLoading}>
                <RefreshIcon sx={{ color: 'primary.main' }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box
          sx={{
            bgcolor: 'common.white',
            borderRadius: '16px',
            p: { xs: 2 },
            minHeight: 200,
          }}>
          {isLoading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

          {!isLoading && logs.length === 0 && (
            <Typography
              sx={{
                color: 'text.secondary',
                fontSize: 16,
                textAlign: 'center',
                py: 8,
              }}>
              No logs available for this application.
            </Typography>
          )}

          {!isLoading && logs.length > 0 && (
            <Box>
              {logs.map((log, index) => (
                <ActivityRow
                  key={`${log.newStatus}-${log.timestamp}-${index}`}
                  log={log}
                  isLast={index === logs.length - 1}
                  isMobile={isMobile}
                />
              ))}
            </Box>
          )}
        </Box>
      </>
    </CommonLayout>
  );
}
